import { useState, useEffect, useDeferredValue, useMemo, lazy, Suspense, useCallback, useRef } from "react";
import { useExploreDefaultLocation } from "@/hooks/useExploreDefaultLocation";
import { parseLocationId, writeStoredLocationId } from "@/lib/exploreDefaultLocation";
import { paramsToScenario, scenarioToParams } from "@/lib/exploreScenarioUrl";
import { ExploreLocationPrompt } from "@/components/locations/ExploreLocationPrompt";
import { Button } from "@/components/ui/button";
import { LocateFixed, Loader2 } from "lucide-react";

const PROMPT_DISMISSED_KEY = 'explore.location.prompt_dismissed';
import { useSearchParams } from "react-router-dom";
import { TABS, RESPONSIVE, SPACING } from "@/design-system";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LPages } from "@/components/i18n";
import { useSEO } from "@/hooks/useSEO";
import { SEOHead } from "@/components/seo/SEOHead";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/admin/primitives/inputs/SearchInput";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, BarChart3, Globe } from "lucide-react";

import { db } from "@/lib/supabaseQuery";
import { TaxExplorer } from "@/components/locations/TaxExplorer";
import { VirtualizedLocationTable } from "@/components/VirtualizedLocationTable";
import { useLocationsWithCoordinates } from "@/hooks/useLocationsWithCoordinates";
import { useLocalizedLocations } from "@/hooks/useLocalizedLocations";
import { calculateCurrentTax } from "@/lib/taxCalculator";
import { UserScenario } from "@/types/taxExplorer";

// Lazy load the comparison chart
const LocationsComparisonChart = lazy(() => import("@/components/locations/LocationsComparisonChart").then(m => ({ default: m.LocationsComparisonChart })));

interface LocationStats {
  location_id: string;
  name: string;
  short_name?: string;
  native_name?: string;
  iso_alpha2: string;
  slug: string;
  native_slug?: string;
  coordinates?: string;
  total_experts: number;
  listed_profiles: number;
  verified_experts: number;
}

const defaultScenario: UserScenario = {
  currentResidencyId: null,
  citizenshipIds: [],
  grossIncome: 100000,
  incomeType: 'employment',
  capitalGains: 0,
  dividends: 0,
  netWorth: 500000,
  plannedDays: 183,
  primaryGoal: 'minimize-tax',
  taxPriorities: [],
  lifestylePrefs: [],
  timeline: 'exploring',
  residencyType: 'permanent',
  incomeSourceType: 'same-as-residence',
  incomeSourceCountryIds: [],
  existingStructures: [],
  structureCountryIds: [],
  familyStatus: 'single',
  dependents: 0,
  industry: 'other'
};

const Explore = () => {
  const { lPages } = useLocalizations();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Default tab is "locations" (first tab); legacy ?tab=insights links fall back to it
  const tabFromUrl = searchParams.get('tab') || 'locations';
  const [activeTab, setActiveTab] = useState(tabFromUrl === 'insights' ? 'locations' : tabFromUrl);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    if (value === 'locations') {
      // Remove tab param for default tab (clean URL)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    } else {
      setSearchParams({ tab: value }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  const seoData = useSEO({
    routeKey: 'explore',
    title: lPages('explore.seo.title', { defaultValue: 'Explore Tax Locations' }),
    description: lPages('explore.seo.description', { defaultValue: 'Compare taxes, rankings and verified tax experts across 250 locations worldwide.' }),
    keywords: [lPages('explore.seo.keywords.jurisdictions', { defaultValue: 'Jurisdictions' }), lPages('explore.seo.keywords.international', { defaultValue: 'International' }), lPages('explore.seo.keywords.global', { defaultValue: 'Global' }), lPages('explore.seo.keywords.country', { defaultValue: 'Country' }), lPages('explore.seo.keywords.havens', { defaultValue: 'Havens' })],
  });
  
  // State for location stats (rankings table)
  const [locationStats, setLocationStats] = useState<LocationStats[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalExperts, setTotalExperts] = useState(0);

  // Shared scenario state (lifted from TaxExplorer)
  const [scenario, setScenario] = useState<UserScenario>(() => ({ ...defaultScenario, ...paramsToScenario(searchParams) }));
  const [activeCategories, setActiveCategories] = useState<string[]>([]);

  // Get locations with coordinates for the map
  const { locations: locationsWithCoords, isLoading: locationsLoading } = useLocationsWithCoordinates();
  
  // Get localized location names
  const { locations: localizedLocations } = useLocalizedLocations();

  // Calculate current tax based on scenario
  const currentTax = useMemo(() => {
    if (!scenario.currentResidencyId || locationsWithCoords.length === 0) return 0;
    return calculateCurrentTax(scenario, locationsWithCoords);
  }, [scenario, locationsWithCoords]);

  // Default location: link > remembered pick > profile > IP > prompt
  const urlLocationId = parseLocationId(searchParams.get('location'));
  const userTouchedRef = useRef(false);
  const [promptDismissed, setPromptDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(PROMPT_DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [resetNonce, setResetNonce] = useState(0);
  const detection = useExploreDefaultLocation(urlLocationId, locationsWithCoords, !locationsLoading, resetNonce);

  useEffect(() => {
    if (userTouchedRef.current || detection.locationId === null) return;
    setScenario(prev => prev.currentResidencyId === detection.locationId
      ? prev
      : { ...prev, currentResidencyId: detection.locationId });
  }, [detection.locationId, resetNonce]);

  const handleScenarioChange = useCallback((updates: Partial<UserScenario>) => {
    if ('currentResidencyId' in updates) {
      userTouchedRef.current = true;
      const id = updates.currentResidencyId ?? null;
      writeStoredLocationId(id);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (id === null) next.delete('location'); else next.set('location', String(id));
        return next;
      }, { replace: true });
    }
    setScenario(prev => ({ ...prev, ...updates }));
  }, [setSearchParams]);

  // Keep income / type / days / net worth in the link so a scenario can be shared
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const isDefault = scenario.grossIncome === defaultScenario.grossIncome && scenario.incomeType === defaultScenario.incomeType
          && scenario.plannedDays === defaultScenario.plannedDays && scenario.netWorth === defaultScenario.netWorth;
        for (const [k, v] of Object.entries(scenarioToParams(scenario))) {
          if (isDefault) next.delete(k); else next.set(k, v);
        }
        return next.toString() === prev.toString() ? prev : next;
      }, { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [scenario.grossIncome, scenario.incomeType, scenario.plannedDays, scenario.netWorth, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScenarioReset = useCallback(() => {
    setScenario(prev => ({ ...defaultScenario, currentResidencyId: prev.currentResidencyId }));
  }, []);

  const handlePromptSelect = useCallback((locationId: number) => {
    handleScenarioChange({ currentResidencyId: locationId });
  }, [handleScenarioChange]);

  const handlePromptDismiss = useCallback(() => {
    setPromptDismissed(true);
    try { sessionStorage.setItem(PROMPT_DISMISSED_KEY, '1'); } catch { /* best-effort */ }
  }, []);

  const handleUseMyLocation = useCallback(() => {
    writeStoredLocationId(null);
    userTouchedRef.current = false;
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('location'); return next; }, { replace: true });
    setScenario(prev => ({ ...prev, currentResidencyId: null }));
    setResetNonce(n => n + 1);
  }, [setSearchParams]);

  const showLocationPrompt = detection.status === 'unresolved'
    && scenario.currentResidencyId === null && !promptDismissed && !userTouchedRef.current;

  useEffect(() => {
    if (localizedLocations.length === 0) return;

    const fetchLocationsData = async () => {
      try {
        setStatsLoading(true);

        const { data: rawLocations, error: locError } = await db.raw
          .from('locations')
          .select('location_id, slug, name, short_name, iso_alpha2, total_users, total_users_profile_listed, total_users_profile_verified, coordinates')
          .not('iso_alpha2', 'in', '(XX,00)')
          .order('total_users', { ascending: false, nullsFirst: false });

        if (locError) throw locError;

        const locationsData: LocationStats[] = (rawLocations || []).map((loc: Record<string, string | number | null>) => {
          const localizedLoc = localizedLocations.find(l => l.iso === loc.iso_alpha2);
          return {
            location_id: localizedLoc?.id || String(loc.location_id),
            name: localizedLoc?.name || String(loc.name || ''),
            // Show the visitor-language name; the stored short name is English-only.
            short_name: localizedLoc?.name || String(loc.short_name || loc.name || ''),
            native_name: localizedLoc?.native || undefined,
            iso_alpha2: String(loc.iso_alpha2 || ''),
            slug: String(loc.slug || ''),
            native_slug: localizedLoc?.slug || String(loc.slug || ''),
            coordinates: loc.coordinates ? String(loc.coordinates) : undefined,
            total_experts: Number(loc.total_users) || 0,
            listed_profiles: Number(loc.total_users_profile_listed) || 0,
            verified_experts: Number(loc.total_users_profile_verified) || 0,
          };
        });

        const total = locationsData?.reduce((sum, loc) => sum + (loc.total_experts || 0), 0) || 0;

        setLocationStats(locationsData || []);
        setTotalExperts(total);
      } catch {
        // Silent fail
      } finally {
        setStatsLoading(false);
      }
    };

    fetchLocationsData();
  }, [localizedLocations]);

  const filteredLocations = useMemo(() => {
    let locations = locationStats;
    
    if (deferredSearchQuery.trim() !== "") {
      const query = deferredSearchQuery.toLowerCase();
      locations = locations.filter(loc => 
        loc.name.toLowerCase().includes(query) ||
        (loc.short_name && loc.short_name.toLowerCase().includes(query)) ||
        (loc.native_name && loc.native_name.toLowerCase().includes(query)) ||
        loc.iso_alpha2.toLowerCase().includes(query)
      );
    }
    return locations;
  }, [deferredSearchQuery, locationStats]);

  const isLoading = locationsLoading || statsLoading;
  const isJurisdictionTab = activeTab === 'locations' || activeTab === 'rankings' || activeTab === 'compare';

  return (
    <>
      <SEOHead {...seoData} />
      <div className={cn("container", RESPONSIVE.pageContainerPy)}>
        <PageHeader
          title={<LPages id="explore.explore_locations" defaultValue="Explore Locations" />}
          subtitle={<LPages id="explore.analyze_rank_and_compare" defaultValue='Analyze performance data, compare key metrics, and see how locations rank.' />}
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className={cn(TABS.contentNested, 'w-full')}>
          <div className={`${RESPONSIVE.headerWithActionsGap3} sm:gap-4 mb-4`}>
            <div className={cn(TABS.scrollWrapper, "flex-shrink-0")}>
              <TabsList className={TABS.list}>
                <TabsTrigger value="locations" className={TABS.triggerResponsive}>
                  <Globe className={TABS.icon} />
                  <span className={RESPONSIVE.tabletUpInline}><LPages id="explore.tabs.locations" defaultValue='Locations' /></span>
                </TabsTrigger>
                <TabsTrigger value="rankings" className={TABS.triggerResponsive}>
                  <Users className={TABS.icon} />
                  <span className={RESPONSIVE.tabletUpInline}><LPages id="explore.tabs.rankings" defaultValue='Rankings' /></span>
                </TabsTrigger>
                <TabsTrigger value="compare" className={TABS.triggerResponsive}>
                  <BarChart3 className={TABS.icon} />
                  <span className={RESPONSIVE.tabletUpInline}><LPages id="explore.tabs.compare" defaultValue='Compare' /></span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Search Bar - only show for Rankings tab */}
            {activeTab === "rankings" && (
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={lPages('locations.search.placeholder', { defaultValue: 'Search locations' })}
                containerClassName="max-w-xs ml-auto"
              />
            )}
          </div>

          {/* Unified Topbar - shared across jurisdiction tabs only */}
          {isJurisdictionTab && (
            <>
              <div className={cn(RESPONSIVE.flexRowCenterGap2, 'mt-2 text-sm text-muted-foreground')} aria-live="polite" data-location-status={detection.status} data-location-source={detection.source}>
                {detection.status === 'detecting' && scenario.currentResidencyId === null ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <LPages id="explore.location_prompt.finding" defaultValue="Finding your location…" />
                  </>
                ) : detection.source !== 'profile' && detection.source !== 'ip' && scenario.currentResidencyId !== null ? (
                  <Button type="button" variant="ghost" size="sm" onClick={handleUseMyLocation}>
                    <LocateFixed className="h-4 w-4 me-1" aria-hidden="true" />
                    <LPages id="explore.location_prompt.use_my_location" defaultValue="Use my location" />
                  </Button>
                ) : null}
              </div>
            </>
          )}

          {/* Locations Tab */}
          <TabsContent value="locations" className={TABS.contentTopLevel}>
            {locationsLoading ? (
              <div className="h-[550px] rounded-lg border bg-muted/20 flex items-center justify-center">
                <div className={cn(SPACING.cardContent, SPACING.stackLg, 'text-center')}>
                  <Skeleton className="h-12 w-12 rounded-full mx-auto" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </div>
              </div>
            ) : (
              <TaxExplorer
                scenario={scenario}
                locations={locationsWithCoords}
                currentTax={currentTax}
                onScenarioChange={handleScenarioChange}
                onReset={handleScenarioReset}
              />
            )}
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings" className={TABS.contentTopLevel}>
            {statsLoading ? (
              <div className="border rounded-lg p-8">
                <div className={cn(SPACING.contentContainer)}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className={cn(RESPONSIVE.flexRowCenterGap4)}>
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-8 w-8" />
                      <div className={cn(SPACING.listItemGap, 'flex-1')}>
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                      <Skeleton className="h-4 w-12" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <VirtualizedLocationTable 
                locations={filteredLocations} 
                totalExperts={totalExperts}
                totalLocations={locationStats.length}
              />
            )}
          </TabsContent>

          <TabsContent value="compare" className={TABS.contentTopLevel}>
            <Suspense fallback={<Skeleton className="w-full h-[400px]" />}>
              <LocationsComparisonChart />
            </Suspense>
          </TabsContent>

        </Tabs>
        <p className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground" role="note">
          <LPages id="explore.disclaimer.beta" defaultValue="Beta tool. Results are estimates for information only and are not legal or tax advice. Consult a qualified tax professional before making decisions." />
        </p>
        <ExploreLocationPrompt
          open={showLocationPrompt}
          locations={locationsWithCoords}
          onSelect={handlePromptSelect}
          onDismiss={handlePromptDismiss}
        />
      </div>
    </>
  );
};

export default Explore;
